import React from 'react';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import { isInternalURL, URLUtils } from '@plone/volto/helpers/Url/Url';
import type { ObjectBrowserItem } from '@plone/types';

export interface MPFLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** URL do destino. Mutuamente exclusivo com `item`. */
  href?: string;
  /** Objeto do backend (object browser, resultado de busca). Exclusivo com `href`. */
  item?: Partial<ObjectBrowserItem> & { remoteUrl?: string };
  /**
   * Dentro do editor de blocos o link vira texto puro: navegar tiraria o
   * editor da página.
   */
  isEditMode?: boolean;
  /**
   * Força o comportamento da aba, sobrepondo a detecção automática:
   * `true` abre link interno em nova aba, `false` mantém link externo na
   * mesma. Deixe `undefined` (padrão) para o `UniversalLink` decidir por
   * `isInternalURL`.
   */
  openInNewTab?: boolean;
  /**
   * Mesmo papel de `openInNewTab`, com o nome que o `UniversalLink` usa —
   * é o que o `MPFButton` declara para funcionar com `as={MPFLink}`.
   */
  openLinkInNewTab?: boolean;
  /**
   * Aviso de "abre em nova aba", ligado por padrão. Sai como `sr-only` dentro
   * do link, ou no fim do `aria-label` quando existe um — o `aria-label`
   * substitui o conteúdo na leitura, então o `sr-only` não seria anunciado.
   */
  hintNewTab?: boolean;
  /** Baixa o arquivo em vez de abri-lo. */
  download?: boolean;
  children: React.ReactNode;
}

/** Texto lido por leitor de tela quando o link troca de aba. */
export const NEW_TAB_HINT = ' (abre em nova aba)';

/**
 * Link único do site público, sobre o `ConditionalLink`/`UniversalLink` do
 * Volto.
 *
 * O `UniversalLink` decide em tempo de execução, por `isInternalURL`: link
 * interno vira navegação client-side (sem recarregar a página, com suporte a
 * âncora `#`), externo vira `<a target="_blank" rel="noopener noreferrer">` e
 * arquivo vira download. Por isso **não** se usa o `Link` do
 * `react-router-dom` para URL vinda do CMS — ela pode ser qualquer uma das
 * três.
 *
 * O que este wrapper acrescenta, e que antes estava copiado em cada card:
 *   - sem destino, ou no editor, renderiza só o conteúdo — nunca um `<a>` sem
 *     `href`, que é anunciado como link mas não é clicável nem focável;
 *   - o aviso acessível de nova aba, emitido só quando a aba de fato troca;
 *   - o repasse de `aria-*`/`data-*`, que a tipagem do `UniversalLink` não
 *     declara (ela só aceita as props que usa) embora o componente espalhe.
 */
export const MPFLink = React.forwardRef<HTMLAnchorElement, MPFLinkProps>(
  function MPFLink(
    {
      href,
      item,
      isEditMode,
      openInNewTab,
      openLinkInNewTab,
      hintNewTab = true,
      download,
      className,
      title,
      onClick,
      children,
      ...rest
    },
    ref,
  ) {
    const forceNewTab = openInNewTab ?? openLinkInNewTab;
    const target = href ?? item?.remoteUrl ?? item?.['@id'];

    if (!target || isEditMode) {
      return <>{children}</>;
    }

    // `mailto:`/`tel:` não são internos nem abrem aba: avisar seria mentira.
    const checked = URLUtils.checkAndNormalizeUrl(target);
    const isExternal =
      !isInternalURL(checked.url) && !checked.isMail && !checked.isTelephone;
    const opensInNewTab = forceNewTab ?? isExternal;

    const hint = hintNewTab && opensInNewTab;
    const ariaLabel = rest['aria-label'];

    return (
      <ConditionalLink
        ref={ref}
        href={href}
        item={item}
        // Precisa continuar `undefined` quando ninguém pediu: o
        // `UniversalLink` trata `false` como "force a mesma aba" e um link
        // externo perderia o `target="_blank"`.
        openLinkInNewTab={forceNewTab}
        download={download}
        className={className}
        title={title}
        onClick={onClick}
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        aria-label={
          hint && ariaLabel ? `${ariaLabel}${NEW_TAB_HINT}` : ariaLabel
        }
      >
        {children}
        {hint && !ariaLabel && <span className="sr-only">{NEW_TAB_HINT}</span>}
      </ConditionalLink>
    );
  },
);

export default MPFLink;
